import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  getAiReferenceConsent,
  updateAiReferenceConsent,
} from '../../../api/personalizationApi';
import { useToast } from '../../../components/Toast';
import { AI_REFERENCE_CONSENT_EVENT } from '../../../utils/aiReferenceConsentCard';
import SettingsSection from './SettingsSection';

export const AI_REFERENCE_SECTION_ID = 'ai-reference';

const ITEMS = [
  {
    key: 'library_auto_collect',
    title: '채팅에 올린 파일 모아 두기',
    caption: '올린 문서를 참고 자료에 모아 두고 AI가 필요할 때 찾아 써요',
    on: '채팅 파일을 참고 자료에 모아요',
    off: '채팅 파일을 모으지 않아요',
  },
  {
    key: 'past_chat_search',
    title: '지난 대화 찾아보기',
    caption: '예전 대화에서 이어 쓸 내용을 찾아요. 학생 이름은 가리고, AI 기억을 끄면 찾지 않아요',
    on: '지난 대화를 찾아볼게요',
    off: '지난 대화를 찾아보지 않아요',
  },
];

/**
 * /chat/settings 의 "AI가 찾아 쓰는 자료" 섹션.
 * 채팅 동의 카드가 두 항목을 한 번에 정하고, 여기서는 하나씩 켜고 끈다.
 * 서버 기능 플래그가 꺼져 있으면 섹션 자체를 그리지 않는다.
 */
const AiReferenceSettings = ({ currentUser, onToggleSection, openSections }) => {
  const { showToast } = useToast();
  const [data, setData] = useState(null);
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await getAiReferenceConsent(currentUser));
      setError('');
    } catch (requestError) {
      setData(null);
      setError(requestError.message);
    }
  }, [currentUser]);

  useEffect(() => {
    load();
  }, [load]);

  if (!data?.feature_enabled) {
    return null;
  }

  const handleToggle = async (item) => {
    if (busyKey) return;
    const nextEnabled = data.consent[item.key]?.enabled !== true;
    const previous = data;
    setBusyKey(item.key);
    setError('');
    setData({
      ...data,
      consent: {
        ...data.consent,
        [item.key]: { ...data.consent[item.key], enabled: nextEnabled },
      },
    });
    try {
      setData(await updateAiReferenceConsent(currentUser, { [item.key]: nextEnabled }, 'settings'));
      window.dispatchEvent(new Event(AI_REFERENCE_CONSENT_EVENT));
      showToast(nextEnabled ? item.on : item.off, 'success');
    } catch (requestError) {
      setData(previous);
      setError(requestError.message);
    } finally {
      setBusyKey('');
    }
  };

  return (
    <SettingsSection
      description="채팅 파일과 지난 대화를 AI가 필요할 때 찾아 써요"
      id={AI_REFERENCE_SECTION_ID}
      onToggle={onToggleSection}
      open={Boolean(openSections[AI_REFERENCE_SECTION_ID])}
      title="AI가 찾아 쓰는 자료"
    >
      <div className="ai-reference-settings">
        {ITEMS.map((item) => {
          const enabled = data.consent[item.key]?.enabled === true;
          return (
            <div className="csettings-master-row" key={item.key}>
              <div>
                <div className="csettings-master-title">{item.title}</div>
                <div className="csettings-master-caption">{item.caption}</div>
              </div>
              <button
                aria-checked={enabled}
                aria-label={item.title}
                className={`personalization-switch ${enabled ? 'is-on' : ''}`}
                disabled={busyKey === item.key}
                onClick={() => handleToggle(item)}
                role="switch"
                type="button"
              >
                <span className="personalization-switch-knob" />
              </button>
            </div>
          );
        })}
        {error && <p className="personalization-error" role="alert">{error}</p>}
        <p className="ai-reference-settings__note">
          끄면 그때부터 모으거나 찾지 않아요. 모아 둔 파일은 참고 자료에서 지울 수 있어요.{' '}
          <Link to="/privacy-policy">개인정보처리방침</Link>
        </p>
      </div>
    </SettingsSection>
  );
};

export default AiReferenceSettings;
